import React, { useState, useEffect } from "react";
import {
  registerUser,
  registerChef,
  getAdminUsers,
  deleteAdminUser,
  updateUserPassword,
} from "../api";

export default function RegisterUser() {
  const [loginValue, setLoginValue] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState("USER");
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState("");

  const [users, setUsers] = useState([]);
  const [editingUserId, setEditingUserId] = useState(null);
  const [newPassword, setNewPassword] = useState("");

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      const list = await getAdminUsers();
      setUsers(list);
    } catch (err) {
      console.error("Error loading users", err);
    }
  };

  // CREATE USER / CHEF
  const handleSubmit = async (e) => {
    e.preventDefault();

    setMessage("");
    setMessageType("");

    const login = loginValue.trim();

    if (!login) {
      setMessage("Username is required.");
      setMessageType("error");
      return;
    }

    if (!password.trim()) {
      setMessage("Password is required.");
      setMessageType("error");
      return;
    }

    try {
      if (role === "CHEF") {
        await registerChef({
          login,
          password,
        });
      } else {
        await registerUser({
          login,
          password,
        });
      }

      // SUCCESS
      setMessage(
        `${role === "CHEF" ? "Chef" : "User"} "${login}" created successfully!`
      );
      setMessageType("success");

      setLoginValue("");
      setPassword("");
      setRole("USER");

      await loadUsers();

    } catch (err) {
      console.error("Create user error:", err);

      /*
       * Backend returns:
       *
       * {
       *   "message": "Username 'chef01' is already taken..."
       * }
       */

      let errorMessage = "Error creating user.";

      if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (typeof err.response?.data === "string") {
        errorMessage = err.response.data;
      } else if (err.message) {
        errorMessage = err.message;
      }

      setMessage(errorMessage);
      setMessageType("error");
    }
  };

  // DELETE USER
  const handleDelete = async (id) => {
    if (!window.confirm("Delete this user?")) return;

    try {
      await deleteAdminUser(id);

      setMessage("User deleted successfully.");
      setMessageType("success");

      await loadUsers();

    } catch (err) {
      console.error("Error deleting user", err);

      const errorMessage =
        err.response?.data?.message ||
        err.response?.data ||
        "Unable to delete user.";

      setMessage(errorMessage);
      setMessageType("error");
    }
  };

  // UPDATE PASSWORD
  const handlePasswordUpdate = async (id) => {
    if (!newPassword.trim()) {
      alert("Enter new password!");
      return;
    }

    try {
      await updateUserPassword(id, newPassword);

      alert("Password updated successfully.");

      setEditingUserId(null);
      setNewPassword("");

    } catch (err) {
      console.error("Password update error", err);

      const errorMessage =
        err.response?.data?.message ||
        err.response?.data ||
        "Failed to update password.";

      alert(errorMessage);
    }
  };

  return (
    <div className="p-8 h-full overflow-auto">

      <h2 className="text-3xl font-bold text-slate-800 mb-6">
        Manage Users
      </h2>

      {/* CREATE USER / CHEF */}
      <form
        onSubmit={handleSubmit}
        className="bg-white shadow rounded p-6 max-w-md space-y-4 mb-10"
      >

        <h3 className="text-xl font-bold mb-4">
          Create New User
        </h3>

        {/* LOGIN */}
        <div>
          <label className="block text-sm font-medium mb-1">
            Username
          </label>

          <input
            type="text"
            className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            value={loginValue}
            onChange={(e) => {
              setLoginValue(e.target.value);
              setMessage("");
              setMessageType("");
            }}
            placeholder="Enter username"
            required
          />
        </div>

        {/* PASSWORD */}
        <div>
          <label className="block text-sm font-medium mb-1">
            Password
          </label>

          <input
            type="password"
            className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
              setMessage("");
              setMessageType("");
            }}
            placeholder="Enter password"
            required
          />
        </div>

        {/* ROLE */}
        <div>
          <label className="block text-sm font-medium mb-1">
            Role
          </label>

          <select
            className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            value={role}
            onChange={(e) => setRole(e.target.value)}
          >
            <option value="USER">USER</option>
            <option value="CHEF">CHEF</option>
          </select>
        </div>

        {/* CREATE BUTTON */}
        <button
          type="submit"
          className="w-full bg-emerald-600 text-white py-2 rounded hover:bg-emerald-700 transition"
        >
          Create {role === "CHEF" ? "Chef" : "User"}
        </button>

        {/* MESSAGE */}
        {message && (
          <div
            className={`mt-3 px-4 py-3 rounded-lg text-sm font-medium ${
              messageType === "success"
                ? "bg-green-100 text-green-700 border border-green-300"
                : "bg-red-100 text-red-700 border border-red-300"
            }`}
          >
            {message}
          </div>
        )}

      </form>

      {/* USERS LIST */}
      <h3 className="text-2xl font-bold mb-3">
        Users List
      </h3>

      <div className="bg-white shadow rounded p-4 overflow-auto">

        <table className="w-full border">

          <thead>
            <tr className="bg-slate-100 text-left">

              <th className="p-2 border">
                ID
              </th>

              <th className="p-2 border">
                Login
              </th>

              <th className="p-2 border">
                Role
              </th>

              <th className="p-2 border">
                Actions
              </th>

            </tr>
          </thead>

          <tbody>

            {users.map((u) => (

              <tr
                key={u.id}
                className="border"
              >

                <td className="p-2 border">
                  {u.id}
                </td>

                <td className="p-2 border font-medium">
                  {u.login}
                </td>

                <td className="p-2 border">

                  <span
                    className={`px-2 py-1 rounded text-xs font-semibold ${
                      u.role === "CHEF"
                        ? "bg-orange-100 text-orange-700"
                        : "bg-blue-100 text-blue-700"
                    }`}
                  >
                    {u.role}
                  </span>

                </td>

                <td className="p-2 border">

                  <div className="flex flex-wrap gap-2">

                    {/* DELETE */}
                    <button
                      className="px-3 py-1 bg-red-500 text-white rounded hover:bg-red-600"
                      onClick={() => handleDelete(u.id)}
                    >
                      Delete
                    </button>

                    {/* PASSWORD */}
                    {editingUserId === u.id ? (

                      <>
                        <input
                          type="password"
                          placeholder="New password"
                          className="border px-2 py-1 rounded"
                          value={newPassword}
                          onChange={(e) =>
                            setNewPassword(e.target.value)
                          }
                        />

                        <button
                          className="px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600"
                          onClick={() =>
                            handlePasswordUpdate(u.id)
                          }
                        >
                          Save
                        </button>

                        <button
                          className="px-3 py-1 bg-gray-400 text-white rounded hover:bg-gray-500"
                          onClick={() => {
                            setEditingUserId(null);
                            setNewPassword("");
                          }}
                        >
                          Cancel
                        </button>
                      </>

                    ) : (

                      <button
                        className="px-3 py-1 bg-yellow-500 text-white rounded hover:bg-yellow-600"
                        onClick={() => {
                          setEditingUserId(u.id);
                          setNewPassword("");
                        }}
                      >
                        Update Password
                      </button>

                    )}

                  </div>

                </td>

              </tr>

            ))}

            {users.length === 0 && (

              <tr>

                <td
                  colSpan="4"
                  className="p-4 text-center text-slate-500"
                >
                  No users created yet.
                </td>

              </tr>

            )}

          </tbody>

        </table>

      </div>

    </div>
  );
}